import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../config/api";
import { FaArrowLeft, FaCheck, FaTimes, FaEdit, FaBullhorn, FaUserTie, FaUsers, FaCalendarAlt, FaListUl, FaLayerGroup, FaDownload } from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";

function ProjectDetailsContainer() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [approved, setApproved] = useState(false);
    const [rejected, setRejected] = useState(false);
    const [modifications, setModifications] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState(null);
    const [comment, setComment] = useState("");
    const [selectedGuide, setSelectedGuide] = useState("");
    const [showGuideDropdown, setShowGuideDropdown] = useState(false);
    const [announcements, setAnnouncements] = useState([]);

    useEffect(() => {
        AOS.init({ duration: 800 });
        const fetchProjectDetails = async () => {
            try {
                const response = await fetch(
                    `${API_BASE_URL}/api/projects/${id}`
                );
                const data = await response.json();
                if (response.ok) {
                    setProject(data);
                }
            } catch (error) {
                console.error("Error fetching project details:", error);
                toast.error("Failed to load project details");
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchProjectDetails();
    }, [id]);

    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const response = await fetch(
                    `${API_BASE_URL}/api/projects/${id}/announcements`
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch announcements");
                }

                const data = await response.json();
                setAnnouncements(data);
            } catch (error) {
                console.error("Error fetching announcements:", error);
            }
        };

        if (id) {
            fetchAnnouncements();
        }
    }, [id]);

    const refreshAnnouncements = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/projects/${id}/announcements`
            );
            if (response.ok) {
                const data = await response.json();
                setAnnouncements(data);
            }
        } catch (error) {
            console.error("Error refreshing announcements:", error);
        }
    };

    const handleNavigationClick = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const handleAccept = () => {
        setApproved(true);
        setModifications(false);
        setRejected(false);
        setComment("");
    };

    const handleReject = () => {
        setRejected(true);
        setModifications(false);
        setApproved(false);
        setComment("");
    };

    const handleModifications = () => {
        setModifications(true);
        setApproved(false);
        setRejected(false);
        setComment("");
    };

    const handleSend = async () => {
        try {
            let action;
            if (approved) action = "accepted";
            if (rejected) action = "rejected";
            if (modifications) action = "modifications";

            if (!action) {
                toast.error("Invalid action selected");
                return;
            }

            const response = await fetch(
                `${API_BASE_URL}/api/projects/${id}/status`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        status: action,
                        message: comment || "",
                        action: action,
                    }),
                }
            );

            if (response.ok) {
                toast.success(`Project ${action} successfully`);
                await refreshAnnouncements();
                handleCancel();
                // Optionally refresh project data or navigate
            } else {
                const errorData = await response.json();
                toast.error(
                    errorData.message || "Error updating project status"
                );
            }
        } catch (error) {
            console.error("Error updating project status:", error);
            toast.error("Error updating project status");
        }
    };

    const handleGuideAssignment = async (guideEmail) => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/projects/${id}/guide`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        guideEmail,
                    }),
                }
            );

            if (response.ok) {
                setConfirmationMessage("Guide assigned successfully");
            }
        } catch (error) {
            console.error("Error assigning guide:", error);
        }
    };

    const handleCancel = () => {
        setApproved(false);
        setRejected(false);
        setModifications(false);
        setComment("");
    };

    const handleAssignGuide = () => {
        setShowGuideDropdown(true);
    };

    const handleGuideSelect = async (e) => {
        const selectedValue = e.target.value;
        if (!selectedValue) return;

        try {
            await handleGuideAssignment(selectedValue);
            await refreshAnnouncements();
            toast.success("Guide assigned successfully");
        } catch (error) {
            toast.error("Error assigning guide");
        }
        setShowGuideDropdown(false);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <div className="w-12 h-12 border-4 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 font-medium">Loading project details...</p>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <div className="text-2xl font-bold text-gray-800 mb-2">Project not found</div>
                <button
                    onClick={() => navigate("/pc/project")}
                    className="text-[#B7202E] hover:underline"
                >
                    Back to Listings
                </button>
            </div>
        );
    }

    const navigationItems = [
        { label: "Project Title", id: "project-title", icon: <FaListUl /> },
        { label: "Category", id: "category", icon: <FaLayerGroup /> },
        { label: "Objectives", id: "objectives", icon: <FaCheck /> },
        { label: "Idea & Inspiration", id: "idea-inspiration", icon: <FaBullhorn /> },
        { label: "Literature Survey", id: "literature-survey", icon: <FaListUl /> },
        { label: "References", id: "references", icon: <FaLayerGroup /> },
        { label: "Team Members", id: "team-members", icon: <FaUsers /> },
        { label: "Guide Preferences", id: "guide", icon: <FaUserTie /> },
        { label: "Co-Guide Preferences", id: "co-guides", icon: <FaUserTie /> },
        { label: "Objectives for 6th sem", id: "objectives1", icon: <FaCalendarAlt /> },
        { label: "Objectives for 7th sem", id: "objectives2", icon: <FaCalendarAlt /> },
    ];

    return (
        <div className="min-h-screen p-4 md:p-8 font-sans">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4" data-aos="fade-down">
                    <div>
                        <button
                            onClick={() => navigate("/pc/project")}
                            className="flex items-center gap-2 text-gray-500 hover:text-[#B7202E] mb-2 transition-colors text-sm font-medium"
                        >
                            <FaArrowLeft /> Back to Project List
                        </button>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Project Details</h1>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Navigation Sidebar */}
                    <div className="hidden lg:block w-64 flex-shrink-0">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sticky top-24" data-aos="fade-right">
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 pl-2">Navigation</h3>
                            <ul className="space-y-1">
                                {navigationItems.map((item, index) => (
                                    <li
                                        key={index}
                                        onClick={() => handleNavigationClick(item.id)}
                                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-gray-600 hover:bg-red-50 hover:text-[#B7202E] transition-all font-medium text-sm"
                                    >
                                        <span className="text-gray-400">{item.icon}</span>
                                        {item.label}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 space-y-6">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8" data-aos="fade-up">

                            {/* Project Title */}
                            <div id="project-title" className="mb-8 border-b border-gray-100 pb-6">
                                <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-2">Project Title</h3>
                                <div className="text-xl md:text-2xl font-bold text-gray-800 leading-tight">
                                    {project.projectTitle}
                                </div>
                            </div>

                            {/* Category */}
                            <div id="category" className="mb-8 border-b border-gray-100 pb-6">
                                <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-2">Category</h3>
                                <div className="text-gray-700 font-medium">
                                    {Object.entries(project.category)
                                        .filter(([_, value]) => value)
                                        .map(([key]) => key)
                                        .join(", ") || "None selected"}
                                </div>
                            </div>

                            {/* Objectives */}
                            <div id="objectives" className="mb-8 border-b border-gray-100 pb-6">
                                <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-2">Objectives</h3>
                                <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                                    {project.objectives}
                                </div>
                            </div>

                            {/* Idea & Inspiration */}
                            <div id="idea-inspiration" className="mb-8 border-b border-gray-100 pb-6">
                                <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-2">Idea & Inspiration</h3>
                                <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                                    {project.ideaInspiration || "No details provided"}
                                </div>
                            </div>

                            {/* Literature Survey */}
                            <div id="literature-survey" className="mb-8 border-b border-gray-100 pb-6">
                                <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-2">Literature Survey</h3>
                                <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                                    {project.literatureSurvey || "No details provided"}
                                </div>
                            </div>

                            {/* References */}
                            <div id="references" className="mb-8 border-b border-gray-100 pb-6">
                                <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-2">References</h3>
                                <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                                    {project.references || "No details provided"}
                                </div>
                            </div>

                            {/* Team Members */}
                            <div id="team-members" className="mb-8 border-b border-gray-100 pb-6">
                                <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3">Team Members</h3>
                                <div className="flex flex-wrap gap-2">
                                    {project.teamMembers?.map((member, index) => (
                                        <div key={index} className="bg-gray-50 text-gray-700 px-4 py-2 rounded-lg border border-gray-200 font-medium text-sm flex items-center gap-2">
                                            <FaUserTie className="text-[#B7202E]" /> {member}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Guide Preferences */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 border-b border-gray-100 pb-6">
                                <div id="guide">
                                    <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3">Guide Preferences</h3>
                                    <div className="space-y-2">
                                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Preference 1</span>
                                            <span className="font-medium text-gray-800">{project.guide.preference1 || "N/A"}</span>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Preference 2</span>
                                            <span className="font-medium text-gray-800">{project.guide.preference2 || "N/A"}</span>
                                        </div>
                                    </div>
                                </div>

                                <div id="co-guides">
                                    <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3">Co-Guide Preferences</h3>
                                    <div className="space-y-2">
                                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Preference 1</span>
                                            <span className="font-medium text-gray-800">{project.coGuides.preference1 || "N/A"}</span>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Preference 2</span>
                                            <span className="font-medium text-gray-800">{project.coGuides.preference2 || "N/A"}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Term Objectives */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 border-b border-gray-100 pb-6">
                                <div id="objectives1">
                                    <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-2">Objectives for 6th sem</h3>
                                    <div className="text-gray-700 text-sm leading-relaxed p-4 bg-gray-50 rounded-lg border border-gray-100 h-full">
                                        {project.objectivesTerm1 || "No objectives defined"}
                                    </div>
                                </div>
                                <div id="objectives2">
                                    <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-2">Objectives for 7th sem</h3>
                                    <div className="text-gray-700 text-sm leading-relaxed p-4 bg-gray-50 rounded-lg border border-gray-100 h-full">
                                        {project.objectivesTerm2 || "No objectives defined"}
                                    </div>
                                </div>
                            </div>

                            {/* Guide Assignment */}
                            <div className="mb-6">
                                <div className="flex items-center gap-4 flex-wrap">
                                    {!showGuideDropdown ? (
                                        <button
                                            className="bg-gray-800 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-black transition-colors flex items-center gap-2 shadow-sm"
                                            onClick={handleAssignGuide}
                                        >
                                            <FaUserTie /> Assign Guide
                                        </button>
                                    ) : (
                                        <div className="flex-1 max-w-md">
                                            <select
                                                value={selectedGuide}
                                                onChange={handleGuideSelect}
                                                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] text-gray-700"
                                            >
                                                <option value="">Select a Guide to Assign...</option>
                                                {project.guide?.preference1 && <option value={project.guide.preference1}>Guide 1: {project.guide.preference1}</option>}
                                                {project.guide?.preference2 && <option value={project.guide.preference2}>Guide 2: {project.guide.preference2}</option>}
                                                {project.coGuides?.preference1 && <option value={project.coGuides.preference1}>Co-Guide 1: {project.coGuides.preference1}</option>}
                                                {project.coGuides?.preference2 && <option value={project.coGuides.preference2}>Co-Guide 2: {project.coGuides.preference2}</option>}
                                                {project.coGuides?.preference3 && <option value={project.coGuides.preference3}>Co-Guide 3: {project.coGuides.preference3}</option>}
                                                {project.coGuides?.preference4 && <option value={project.coGuides.preference4}>Co-Guide 4: {project.coGuides.preference4}</option>}
                                            </select>
                                        </div>
                                    )}
                                    <button
                                        className="text-gray-500 hover:text-gray-700 underline text-sm"
                                        onClick={() => setShowGuideDropdown(false)}
                                    >
                                        {showGuideDropdown && "Cancel"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Announcements Section */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8" data-aos="fade-up">
                            <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                <FaBullhorn className="text-[#B7202E]" /> Project Announcements
                            </h3>

                            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                                {announcements.length > 0 ? (
                                    announcements.map((announcement, index) => (
                                        <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                            <div className="flex justify-between items-start mb-2">
                                                <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide border
                                                    ${announcement.type === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                                                        announcement.type === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                                                            'bg-blue-100 text-blue-700 border-blue-200'}`}>
                                                    {announcement.type}
                                                </span>
                                                <span className="text-xs text-gray-500 font-medium">
                                                    {new Date(announcement.date).toLocaleDateString("en-IN", {
                                                        day: 'numeric', month: 'numeric', year: 'numeric'
                                                    })}
                                                </span>
                                            </div>
                                            <p className="text-gray-700 text-sm leading-relaxed">
                                                {announcement.message}
                                            </p>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                                        No announcements yet
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg md:relative md:bg-transparent md:border-0 md:p-0 md:shadow-none z-10">
                            <div className="max-w-7xl mx-auto flex gap-3 md:justify-end">
                                <button
                                    className="flex-1 md:flex-none bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold shadow-sm transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                                    onClick={handleAccept}
                                >
                                    <FaCheck /> Accept
                                </button>
                                <button
                                    className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold shadow-sm transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                                    onClick={handleModifications}
                                >
                                    <FaEdit /> Modify
                                </button>
                                <button
                                    className="flex-1 md:flex-none bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold shadow-sm transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                                    onClick={handleReject}
                                >
                                    <FaTimes /> Reject
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals for Actions */}
            {(approved || rejected || modifications) && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100">
                        <div className={`p-4 text-white font-bold text-lg flex justify-between items-center
                            ${approved ? 'bg-green-600' : rejected ? 'bg-red-600' : 'bg-blue-600'}`}>
                            <span>
                                {approved ? 'Approve Project' : rejected ? 'Reject Project' : 'Request Modifications'}
                            </span>
                            <button onClick={handleCancel} className="text-white/80 hover:text-white"><FaTimes /></button>
                        </div>

                        <div className="p-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {approved ? 'Add a comment (optional)' : 'Reason for decision'}
                            </label>
                            <textarea
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1 h-32 resize-none text-sm"
                                placeholder={approved ? "Great work! Approved." : "Please explain why..."}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                style={{
                                    '--tw-ring-color': approved ? '#16a34a' : rejected ? '#dc2626' : '#2563eb'
                                }}
                            ></textarea>

                            <div className="flex gap-3 mt-6 justify-end">
                                <button
                                    className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={`px-6 py-2 text-white font-bold rounded-lg shadow-sm transition-transform hover:-translate-y-0.5
                                        ${approved ? 'bg-green-600 hover:bg-green-700' :
                                            rejected ? 'bg-red-600 hover:bg-red-700' :
                                                'bg-blue-600 hover:bg-blue-700'}`}
                                    onClick={handleSend}
                                >
                                    Confirm Decision
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProjectDetailsContainer;
